import styles from './TypographyPage.module.css';

export const TypographyPage = () => {
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Typography</h1>
                <p className={styles.description}>
                    Guía de tipografía del sistema Zafiro ERP. Fuentes, tamaños, pesos y
                    estilos para mantener consistencia visual en toda la aplicación.
                </p>
            </div>

            {/* Fuente del Sistema */}
            <section className={styles.section}>
                <h2>Fuente del Sistema</h2>
                <p>Zafiro ERP utiliza la pila de fuentes del sistema para mejor rendimiento y experiencia nativa.</p>

                <div className={styles.fontStack}>
                    <h3>Base (Sans-serif)</h3>
                    <code className={styles.code}>
                        {`font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 
'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 
'Droid Sans', 'Helvetica Neue', sans-serif;`}
                    </code>
                    <p className={styles.note}>
                        Esta pila garantiza que se use la mejor fuente disponible en cada sistema operativo.
                    </p>

                    <h3 style={{ marginTop: '1.5rem' }}>Monospace (Código)</h3>
                    <code className={styles.code} style={{ fontFamily: 'monospace' }}>
                        {`font-family: 'Fira Code', 'Consolas', 'Monaco', 
'Courier New', monospace;`}
                    </code>
                    <p className={styles.note}>
                        Para código, bloques <code style={{ background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>code</code> y elementos técnicos.
                    </p>

                    <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--bg-hover)', borderRadius: '8px' }}>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                            💡 <strong>Referencia:</strong> Estas fuentes están definidas en{' '}
                            <code style={{ background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                                shared/styles/_variables.css
                            </code>
                            {' '}como{' '}
                            <code style={{ background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                                --font-family-base
                            </code>
                            {' '}y{' '}
                            <code style={{ background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                                --font-family-mono
                            </code>
                        </p>
                    </div>
                </div>
            </section>

            {/* Headings */}
            <section className={styles.section}>
                <h2>Encabezados (Headings)</h2>
                <p>Jerarquía de 6 niveles para títulos y secciones.</p>

                <div className={styles.headingsShowcase}>
                    <div className={styles.headingExample}>
                        <h1 className={styles.h1}>Heading 1 - Título Principal</h1>
                        <div className={styles.specs}>
                            <code>&lt;h1&gt;</code>
                            <span>2.5rem / 40px | Font-weight: 700</span>
                        </div>
                    </div>

                    <div className={styles.headingExample}>
                        <h2 className={styles.h2}>Heading 2 - Sección Principal</h2>
                        <div className={styles.specs}>
                            <code>&lt;h2&gt;</code>
                            <span>2rem / 32px | Font-weight: 600</span>
                        </div>
                    </div>

                    <div className={styles.headingExample}>
                        <h3 className={styles.h3}>Heading 3 - Subsección</h3>
                        <div className={styles.specs}>
                            <code>&lt;h3&gt;</code>
                            <span>1.75rem / 28px | Font-weight: 600</span>
                        </div>
                    </div>

                    <div className={styles.headingExample}>
                        <h4 className={styles.h4}>Heading 4 - Título Menor</h4>
                        <div className={styles.specs}>
                            <code>&lt;h4&gt;</code>
                            <span>1.5rem / 24px | Font-weight: 600</span>
                        </div>
                    </div>

                    <div className={styles.headingExample}>
                        <h5 className={styles.h5}>Heading 5 - Subtítulo</h5>
                        <div className={styles.specs}>
                            <code>&lt;h5&gt;</code>
                            <span>1.25rem / 20px | Font-weight: 600</span>
                        </div>
                    </div>

                    <div className={styles.headingExample}>
                        <h6 className={styles.h6}>Heading 6 - Etiqueta</h6>
                        <div className={styles.specs}>
                            <code>&lt;h6&gt;</code>
                            <span>1rem / 16px | Font-weight: 600</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Body Text */}
            <section className={styles.section}>
                <h2>Texto de Cuerpo (Body Text)</h2>
                <p>Tamaños de texto para contenido general.</p>

                <div className={styles.bodyTextShowcase}>
                    <div className={styles.textExample}>
                        <p className={styles.textLarge}>
                            Texto Large - Este es un texto grande usado para introducir secciones o destacar información importante.
                        </p>
                        <div className={styles.specs}>
                            <code>.text-large</code>
                            <span>1.125rem / 18px | Line-height: 1.6</span>
                        </div>
                    </div>

                    <div className={styles.textExample}>
                        <p className={styles.textBase}>
                            Texto Base - Este es el tamaño estándar de texto usado para la mayoría del contenido.
                            Es cómodo de leer y proporciona buena legibilidad en pantallas de todos los tamaños.
                        </p>
                        <div className={styles.specs}>
                            <code>.text-base</code>
                            <span>1rem / 16px | Line-height: 1.5</span>
                        </div>
                    </div>

                    <div className={styles.textExample}>
                        <p className={styles.textSmall}>
                            Texto Small - Usado para texto secundario, notas, timestamps o información complementaria que no requiere tanto énfasis.
                        </p>
                        <div className={styles.specs}>
                            <code>.text-small</code>
                            <span>0.875rem / 14px | Line-height: 1.4</span>
                        </div>
                    </div>

                    <div className={styles.textExample}>
                        <p className={styles.textXSmall}>
                            Texto Extra Small - Para casos muy específicos como etiquetas diminutas, footnotes o metadata.
                        </p>
                        <div className={styles.specs}>
                            <code>.text-xs</code>
                            <span>0.75rem / 12px | Line-height: 1.3</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Font Weights */}
            <section className={styles.section}>
                <h2>Pesos de Fuente (Font Weights)</h2>
                <p>Diferentes pesos para jerarquía y énfasis.</p>

                <div className={styles.weightsShowcase}>
                    <div className={styles.weightExample}>
                        <p style={{ fontWeight: 300 }}>Light (300) - Texto ligero</p>
                        <code>font-weight: 300</code>
                    </div>

                    <div className={styles.weightExample}>
                        <p style={{ fontWeight: 400 }}>Regular (400) - Texto normal</p>
                        <code>font-weight: 400 | var(--font-weight-normal)</code>
                    </div>

                    <div className={styles.weightExample}>
                        <p style={{ fontWeight: 500 }}>Medium (500) - Texto medio</p>
                        <code>font-weight: 500 | var(--font-weight-medium)</code>
                    </div>

                    <div className={styles.weightExample}>
                        <p style={{ fontWeight: 600 }}>Semibold (600) - Texto semi-negrita</p>
                        <code>font-weight: 600 | var(--font-weight-semibold)</code>
                    </div>

                    <div className={styles.weightExample}>
                        <p style={{ fontWeight: 700 }}>Bold (700) - Texto negrita</p>
                        <code>font-weight: 700 | var(--font-weight-bold)</code>
                    </div>
                </div>
            </section>

            {/* Text Styles */}
            <section className={styles.section}>
                <h2>Estilos de Texto</h2>
                <p>Decoraciones y transformaciones de texto.</p>

                <div className={styles.stylesShowcase}>
                    <div className={styles.styleExample}>
                        <p><strong>Texto en Negrita</strong></p>
                        <code>&lt;strong&gt; o font-weight: 700</code>
                    </div>

                    <div className={styles.styleExample}>
                        <p><em>Texto en Cursiva</em></p>
                        <code>&lt;em&gt; o font-style: italic</code>
                    </div>

                    <div className={styles.styleExample}>
                        <p><u>Texto Subrayado</u></p>
                        <code>&lt;u&gt; o text-decoration: underline</code>
                    </div>

                    <div className={styles.styleExample}>
                        <p className={styles.strikethrough}>Texto Tachado</p>
                        <code>&lt;s&gt; o text-decoration: line-through</code>
                    </div>

                    <div className={styles.styleExample}>
                        <p className={styles.uppercase}>Texto en Mayúsculas</p>
                        <code>text-transform: uppercase</code>
                    </div>

                    <div className={styles.styleExample}>
                        <p className={styles.capitalize}>texto capitalizado</p>
                        <code>text-transform: capitalize</code>
                    </div>
                </div>
            </section>

            {/* Line Heights */}
            <section className={styles.section}>
                <h2>Alturas de Línea (Line Height)</h2>
                <p>Espaciado vertical para mejor legibilidad.</p>

                <div className={styles.lineHeightsShowcase}>
                    <div className={styles.lineHeightExample}>
                        <p style={{ lineHeight: 1.3 }}>
                            Line Height 1.3 - Para headings y títulos donde necesitamos un espaciado más compacto.
                        </p>
                        <code>line-height: 1.3</code>
                    </div>

                    <div className={styles.lineHeightExample}>
                        <p style={{ lineHeight: 1.5 }}>
                            Line Height 1.5 - Para texto normal de cuerpo. Este es el estándar que proporciona
                            buena legibilidad sin desperdiciar espacio vertical.
                        </p>
                        <code>line-height: 1.5</code>
                    </div>

                    <div className={styles.lineHeightExample}>
                        <p style={{ lineHeight: 1.8 }}>
                            Line Height 1.8 - Para texto largo o artículos donde queremos maximizar la legibilidad
                            y comodidad de lectura, especialmente en párrafos extensos.
                        </p>
                        <code>line-height: 1.8</code>
                    </div>
                </div>
            </section>

            {/* Ejemplos Prácticos */}
            <section className={styles.section}>
                <h2>Ejemplo Práctico</h2>

                <div className={styles.practicalExample}>
                    <h3>Título del Artículo</h3>
                    <p className={styles.metadata}>
                        <span>Autor: Juan Pérez</span>
                        <span>•</span>
                        <span>12 Dic 2024</span>
                        <span>•</span>
                        <span>5 min lectura</span>
                    </p>

                    <p className={styles.lead}>
                        Este es un párrafo de introducción que destaca del resto del contenido.
                        Usa un tamaño de fuente ligeramente mayor para captar la atención.
                    </p>

                    <p>
                        Este es contenido normal de párrafo con el tamaño base de fuente.
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod
                        tempor incididunt ut labore et dolore magna aliqua.
                    </p>

                    <p>
                        Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi
                        ut aliquip ex ea commodo consequat. <strong>Texto importante en negrita</strong>.
                        Duis aute irure dolor in reprehenderit.
                    </p>

                    <blockquote className={styles.blockquote}>
                        "Esta es una cita destacada con estilos especiales para diferenciarse
                        del contenido regular."
                    </blockquote>

                    <p className={styles.small}>
                        Nota al pie o texto secundario en tamaño pequeño para información complementaria.
                    </p>
                </div>
            </section>

            {/* CSS Variables */}
            <section className={styles.section}>
                <h2>CSS Variables</h2>
                <p>Todas las variables tipográficas están definidas en <code style={{ background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>shared/styles/_variables.css</code></p>

                <h3>Fuentes</h3>
                <code className={styles.code}>
                    {`/* Familias de Fuentes */
--font-family-base: -apple-system, BlinkMacSystemFont, 'Segoe UI', 
  'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 
  'Droid Sans', 'Helvetica Neue', sans-serif;
--font-family-mono: 'Fira Code', 'Consolas', 'Monaco', 
  'Courier New', monospace;`}
                </code>

                <h3 style={{ marginTop: '2rem' }}>Tamaños de Fuente</h3>
                <code className={styles.code}>
                    {`/* Tamaños */
--font-size-xs: 0.75rem;    /* 12px */
--font-size-sm: 0.875rem;   /* 14px */
--font-size-base: 1rem;     /* 16px */
--font-size-lg: 1.125rem;   /* 18px */
--font-size-xl: 1.25rem;    /* 20px */
--font-size-2xl: 1.5rem;    /* 24px */
--font-size-3xl: 1.875rem;  /* 30px */
--font-size-4xl: 2.25rem;   /* 36px */
--font-size-5xl: 2.75rem;   /* 44px */
--font-size-7xl: 3.25rem;   /* 52px */`}
                </code>

                <h3 style={{ marginTop: '2rem' }}>Pesos de Fuente</h3>
                <code className={styles.code}>
                    {`/* Pesos */
--font-weight-light: 300;
--font-weight-normal: 400;
--font-weight-medium: 500;
--font-weight-semibold: 600;
--font-weight-bold: 700;
--font-weight-extrabold: 800;`}
                </code>

                <h3 style={{ marginTop: '2rem' }}>Line Heights</h3>
                <code className={styles.code}>
                    {`/* Alturas de Línea */
--line-height-tight: 1.25;
--line-height-normal: 1.5;
--line-height-relaxed: 1.75;`}
                </code>

                <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '8px', borderLeft: '4px solid var(--color-info)' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>📁 Archivos de Estilos Globales</h4>
                    <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--text-secondary)' }}>
                        <li><code>shared/styles/_variables.css</code> - Variables CSS (colores, fuentes, espaciado)</li>
                        <li><code>shared/styles/_themes.css</code> - Temas (light/dark)</li>
                        <li><code>shared/styles/_globals.css</code> - Estilos globales (reset, typography)</li>
                        <li><code>shared/styles/_utilities.css</code> - Clases utilitarias</li>
                        <li><code>shared/styles/index.css</code> - Importa todos los archivos</li>
                    </ul>
                </div>
            </section>

            {/* Logos */}
            <section className={styles.section}>
                <h2>Logos del Sistema</h2>
                <p>Los logos de Zafiro ERP están disponibles en <code style={{ background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>shared/icons/</code></p>

                <div className={styles.logosShowcase}>
                    <div className={styles.logoExample}>
                        <div className={styles.logoPreview} style={{ background: 'var(--bg-primary)' }}>
                            <img src="/src/shared/icons/logo_icon.svg" alt="Logo Icon" style={{ width: '64px', height: '64px' }} />
                        </div>
                        <div className={styles.logoInfo}>
                            <h4>Logo Icon</h4>
                            <code>logo_icon.svg</code>
                            <p>Ícono del logo para favicon, apps móviles</p>
                        </div>
                    </div>

                    <div className={styles.logoExample}>
                        <div className={styles.logoPreview} style={{ background: 'white' }}>
                            <img src="/src/shared/icons/logo_text_dark.svg" alt="Logo Dark" style={{ height: '48px' }} />
                        </div>
                        <div className={styles.logoInfo}>
                            <h4>Logo Dark</h4>
                            <code>logo_text_dark.svg</code>
                            <p>Logo con texto para tema claro</p>
                        </div>
                    </div>

                    <div className={styles.logoExample}>
                        <div className={styles.logoPreview} style={{ background: '#1a1a1a' }}>
                            <img src="/src/shared/icons/logo_text_light.svg" alt="Logo Light" style={{ height: '48px' }} />
                        </div>
                        <div className={styles.logoInfo}>
                            <h4>Logo Light</h4>
                            <code>logo_text_light.svg</code>
                            <p>Logo con texto para tema oscuro</p>
                        </div>
                    </div>
                </div>

                <h3 style={{ marginTop: '2rem' }}>Uso en React</h3>
                <code className={styles.code}>
                    {`import logoIcon from '@/shared/icons/logo_icon.svg';
import logoTextLight from '@/shared/icons/logo_text_light.svg';
import logoTextDark from '@/shared/icons/logo_text_dark.svg';

function Header() {
  const { theme } = useTheme();
  
  return (
    <div className="header">
      <img src={logoIcon} alt="Zafiro" />
      <img 
        src={theme === 'dark' ? logoTextLight : logoTextDark} 
        alt="Zafiro ERP" 
      />
    </div>
  );
}
`}
                </code>
            </section>

        </div>
    );
};
